import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { to: "/", label: "首页", exact: true },
  { to: "/category", label: "影视分类", exact: false },
  { to: "/special", label: "专题", exact: false },
  { to: "/about", label: "关于", exact: false },
];

const categoryChildren = [
  { to: "/category?cat=电视剧", label: "电视剧" },
  { to: "/category?cat=电影", label: "电影" },
  { to: "/category?cat=综艺", label: "综艺" },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileSearch, setMobileSearch] = useState("");
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (value: string) => {
    const key = value.trim();
    if (!key) return;
    navigate(`/search?q=${encodeURIComponent(key)}`);
    setKeyword("");
    setMobileSearch("");
    setSearchOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full">
      <div
        className={`w-full border-b transition-colors duration-300 ${
          scrolled
            ? "border-background-200 bg-background-50/95 backdrop-blur"
            : "border-transparent bg-background-50"
        }`}
      >
        <div className="mx-auto flex h-16 w-full max-w-[1280px] items-center gap-4 px-4 md:h-20 md:px-6">
          <Link to="/" className="flex shrink-0 items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-movie-2-line text-lg"></i>
            </span>
            <span className="font-heading text-lg font-bold tracking-tight text-foreground-950 md:text-xl">
              善良的嫂子
            </span>
          </Link>

          <nav className="ml-2 hidden items-center gap-1 md:flex lg:ml-6">
            {navItems.map((item) =>
              item.to === "/category" ? (
                <div key={item.to} className="group relative">
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      `flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                        isActive || location.pathname.startsWith("/detail") || location.pathname.startsWith("/actor")
                          ? "text-primary-600"
                          : "text-foreground-700 hover:text-primary-600"
                      }`
                    }
                  >
                    {item.label}
                    <i className="ri-arrow-down-s-line text-base"></i>
                  </NavLink>
                  <div className="invisible absolute left-0 top-full w-36 translate-y-1 rounded-lg border border-background-200 bg-background-50 p-1 opacity-0 transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                    {categoryChildren.map((child) => (
                      <Link
                        key={child.to}
                        to={child.to}
                        className="block rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-600"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.exact}
                  className={({ isActive }) =>
                    `rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                      isActive ? "text-primary-600" : "text-foreground-700 hover:text-primary-600"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ),
            )}
          </nav>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              submitSearch(keyword);
            }}
            className="ml-auto hidden items-center gap-2 rounded-full border border-background-200 bg-background-100 px-3 py-1.5 md:flex"
          >
            <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base"></i>
            </span>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索剧名、演员"
              className="w-32 bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400 lg:w-44"
            />
            <button
              type="submit"
              className="whitespace-nowrap rounded-full bg-primary-500 px-3 py-1 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>

          <div className="ml-auto flex items-center gap-1 md:ml-1">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label="打开搜索"
              className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-100 md:hidden"
            >
              <i className="ri-search-line text-xl"></i>
            </button>
            <button
              type="button"
              onClick={() => setMobileOpen((prev) => !prev)}
              aria-label="打开菜单"
              className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-800 transition-colors hover:bg-background-100 md:hidden"
            >
              <i className={mobileOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-b border-background-200 bg-background-50 px-4 py-3 md:hidden">
          <nav className="flex flex-col">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                className={({ isActive }) =>
                  `rounded-md px-3 py-3 text-base font-medium transition-colors ${
                    isActive ? "bg-primary-50 text-primary-600" : "text-foreground-800 hover:bg-background-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="mt-2 border-t border-background-200 pt-3">
              <p className="px-3 pb-2 text-xs text-foreground-400">分类快捷入口</p>
              <div className="flex flex-wrap gap-2 px-3">
                {categoryChildren.map((child) => (
                  <Link
                    key={child.to}
                    to={child.to}
                    className="rounded-full bg-secondary-100 px-3 py-1.5 text-sm text-secondary-900"
                  >
                    {child.label}
                  </Link>
                ))}
              </div>
            </div>
          </nav>
        </div>
      )}

      {searchOpen && (
        <div className="fixed inset-0 z-[60] bg-background-50 px-4 py-4 md:hidden">
          <div className="flex items-center gap-2">
            <form
              onSubmit={(event) => {
                event.preventDefault();
                submitSearch(mobileSearch);
              }}
              className="flex flex-1 items-center gap-2 rounded-full border border-background-200 bg-background-100 px-4 py-2"
            >
              <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base"></i>
              </span>
              <input
                autoFocus
                value={mobileSearch}
                onChange={(event) => setMobileSearch(event.target.value)}
                placeholder="搜索剧名、演员"
                className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
              />
            </form>
            <button
              type="button"
              onClick={() => setSearchOpen(false)}
              className="whitespace-nowrap px-2 py-2 text-sm text-foreground-600"
            >
              取消
            </button>
          </div>
          <div className="mt-6">
            <p className="text-xs text-foreground-400">热门搜索</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {["善良的嫂子", "家庭情感", "悬疑", "综艺", "林清婉"].map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => submitSearch(tag)}
                  className="rounded-full bg-secondary-100 px-4 py-2 text-sm text-secondary-900"
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}